import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { SlideLayout } from '../SlideLayout';
import { Cpu, MemoryStick } from 'lucide-react';
export function DecodeStageSlide() {
  return <SlideLayout slideNumber={10} totalSlides={20} title="The Core Argument" data-id="element-153">
      <div className="w-full h-full flex flex-col" data-id="element-154">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-5" data-id="element-155">
          <h2 className="text-4xl md:text-5xl font-bold mb-2" data-id="element-156">
            Decode Is <span className="text-brand-primary" data-id="element-157">Memory-Bound</span>
          </h2>
          <p className="text-xl text-brand-muted max-w-3xl mx-auto" data-id="element-158">
            LLM inference has two phases — and the one users actually feel is
            where workload type determines the winner.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 flex-1 mb-4" data-id="element-159">
          {/* Prefill */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2
        }} className="bg-brand-surface border border-chip-gpu/20 rounded-2xl p-6 flex flex-col justify-between" data-id="element-160">
            <div data-id="element-161">
              <div className="flex items-center gap-3 mb-4" data-id="element-162">
                <div className="p-2.5 bg-chip-gpu/10 rounded-lg text-chip-gpu" data-id="element-163">
                  <Cpu size={28} data-id="element-164" />
                </div>
                <h3 className="text-2xl font-bold text-chip-gpu" data-id="element-165">
                  Prefill Phase
                </h3>
              </div>
              <div className="space-y-3 text-brand-muted" data-id="element-166">
                <p className="text-lg" data-id="element-167">Processing input tokens in parallel</p>
                <div className="bg-brand-dark rounded-lg p-3 font-mono text-base" data-id="element-168">
                  <span className="text-chip-gpu" data-id="element-169">Compute-bound</span> — Matrix
                  × Matrix ops
                </div>
                <ul className="space-y-1.5 text-base" data-id="element-170">
                  <li className="flex items-start gap-2" data-id="element-171">
                    <span className="text-chip-gpu mt-1 shrink-0" data-id="element-172">•</span>
                    <span data-id="element-173">
                      Massive parallelism plays to GPU strengths. Thousands of
                      CUDA cores fully utilized.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-brand-border" data-id="element-174">
              <span className="text-chip-gpu font-bold text-xl" data-id="element-175">GPU Wins</span>
              <span className="text-base text-brand-muted" data-id="element-176">
                — and we acknowledge this
              </span>
            </div>
          </motion.div>

          {/* Decode */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3
        }} className="bg-brand-surface border border-brand-primary/30 rounded-2xl p-6 relative overflow-hidden flex flex-col justify-between" data-id="element-177">
            <div className="absolute top-0 left-0 w-full h-1 bg-brand-primary" data-id="element-178" />
            <div data-id="element-179">
              <div className="flex items-center gap-3 mb-4" data-id="element-180">
                <div className="p-2.5 bg-brand-primary/10 rounded-lg text-brand-primary" data-id="element-181">
                  <MemoryStick size={28} data-id="element-182" />
                </div>
                <h3 className="text-2xl font-bold text-brand-primary" data-id="element-183">
                  Decode Phase
                </h3>
              </div>
              <div className="space-y-3 text-brand-muted" data-id="element-184">
                <p className="text-lg" data-id="element-185">
                  Generating output tokens one at a time
                </p>
                <div className="bg-brand-dark rounded-lg p-3 font-mono text-base" data-id="element-186">
                  <span className="text-brand-primary" data-id="element-187">
                    Memory-bound at low batch sizes
                  </span>{' '}
                  — Matrix × Vector ops
                </div>
                <ul className="space-y-2 text-base" data-id="element-188">
                  <li className="flex items-start gap-2" data-id="element-189">
                    <span className="text-brand-primary mt-1 shrink-0" data-id="element-190">•</span>
                    <span data-id="element-191">
                      <span className="text-white font-medium" data-id="element-192">
                        Batch 1–8 (real-time):
                      </span>{' '}
                      GPU cores sit{' '}
                      <span className="text-red-400" data-id="element-193">largely idle</span>,
                      burning power for utilization that never materializes.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-194">
                    <span className="text-chip-gpu mt-1 shrink-0" data-id="element-195">•</span>
                    <span data-id="element-196">
                      <span className="text-white font-medium" data-id="element-197">
                        Batch 32+ (bulk throughput):
                      </span>{' '}
                      becomes compute-bound again — GPUs regain advantage.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-brand-border" data-id="element-198">
              <span className="text-brand-primary font-bold text-xl" data-id="element-199">
                FPGA Wins for Real-Time
              </span>
              <span className="text-base text-brand-muted" data-id="element-200">
                — this is what interactive users feel
              </span>
            </div>
          </motion.div>
        </div>

        {/* Key insight callout */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="bg-brand-primary/5 border border-brand-primary/20 rounded-xl p-5" data-id="element-201">
          <p className="text-lg text-white leading-relaxed" data-id="element-202">
            Real-time inference (voice agents, live coding, chatbots) runs at
            batch size 1–8, where decode latency dominates end-to-end
            experience. This is the{' '}
            <span className="text-brand-primary font-semibold" data-id="element-203">
              memory-bandwidth-bound regime
            </span>{' '}
            where FPGAs deliver deterministic low latency at far better power
            efficiency — exactly the workload profile of applications commanding{' '}
            <span className="text-brand-primary font-semibold" data-id="element-204">
              premium pricing tiers
            </span>
            .
          </p>
          <p className="text-base text-brand-muted mt-2" data-id="element-205">
            Sources: [20][21][22]
          </p>
        </motion.div>
      </div>
    </SlideLayout>;
}